import { useLayoutEffect, useState, type ReactNode } from 'react';

import { Box } from '@mui/material';

/**
 * Renders a fixed-width design at its real width, displayed small enough to fit
 * the space it has.
 *
 * The unframed stages set the screen's width directly and refused to shrink, so
 * a 1440 laptop design in the room left beside the Loft menu ran off the side of
 * it: at 100% zoom on a laptop you were looking at part of the screen, and on a
 * narrow window at most half. ComputerFrame already did this for the framed
 * view, which is why only the unframed one was wrong.
 *
 * Scaling rather than reflowing is the point. The design keeps laying out at
 * 1440, so what you judge is the real thing at a smaller size, not a different
 * breakpoint. Only ever scales down: a design smaller than its space is left
 * alone rather than blown up.
 *
 * `width='fill'` is the opposite case and skips all of that: the design takes
 * the room the stage gives it, reflowing as the window changes, so there is no
 * fixed width to scale from.
 */
const ScaleToFit = ({
  width,
  height,
  children,
}: {
  /** Logical width the child lays out at, or 'fill' to take the stage's own
      width and height instead of laying out at a width of its own. */
  width: number | 'fill';
  /** Logical height, so the scaled box takes the right amount of room. */
  height?: number;
  children?: ReactNode;
}) => {
  const [wrapEl, setWrapEl] = useState<HTMLElement | null>(null);
  const [scale, setScale] = useState(1);
  const fixedWidth = width === 'fill' ? null : width;

  useLayoutEffect(() => {
    if (!wrapEl || fixedWidth === null) return;
    const compute = () =>
      setScale(Math.min(1, wrapEl.clientWidth / fixedWidth) || 1);
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapEl);
    return () => observer.disconnect();
  }, [wrapEl, fixedWidth]);

  // Filling the stage: no fixed width to measure against and nothing to scale,
  // so the child is handed the room and left to reflow into it. flex={1} takes
  // the height left under the stage's pills, which is what the child's own
  // height='100%' then resolves against.
  if (fixedWidth === null) {
    return <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>{children}</Box>;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        height: height ? height * scale : undefined,
        flexShrink: 0,
      }}
      ref={setWrapEl}
      // The scaled child no longer contributes its own height, so the wrapper
      // carries it. Without this the stage collapses and the design overlaps
      // whatever sits under it.
    >
      <Box
        sx={{
          width: fixedWidth,
          height: height,
          flexShrink: 0,
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ScaleToFit;
